import React, { ReactElement, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { List, Button, Tag, Typography, Space, Flex, Select, Alert } from 'antd'
import { PiCalendarBlank, PiMapPin, PiX } from 'react-icons/pi'
import { useIntl } from 'react-intl'
import { countriesForPicker, findCountry, placeLabel, subdivisionOptions, subdivisionTermKey, uniqueLocationName } from '@vacationtracker/shared/data/countries'
import { filterCountryOptions } from '@vacationtracker/shared/functions/filter-countries'
import { filterOptions } from '../../util/filter-options'
import { PublicHolidaysRepository } from '@vacationtracker/shared/common/date-holidays-repository'
import { IOnboardingLocation } from './types'
import IntlMessages from '../../util/IntlMessages'
import { FrontendUrls } from '../../types/urls'

const { Text } = Typography
const { Option } = Select

type LocationAddPayload = Pick<IOnboardingLocation, 'name' | 'countryCode' | 'subdivisionCode'>

interface LocationStepProps {
  locations: IOnboardingLocation[]
  locationLimit: number
  onAdd: (location: LocationAddPayload) => void
  onRemove: (id: string) => void
  onConfirm: () => void
  onSkip: () => void
  onNext?: () => void
  readOnly?: boolean
}

const LocationStep = ({ locations, locationLimit, onAdd, onRemove, onConfirm, onSkip, onNext, readOnly }: LocationStepProps): ReactElement => {
  const { formatMessage } = useIntl()
  const [selectedCountryIso, setSelectedCountryIso] = useState<string | undefined>(undefined)
  const [selectedStateIso, setSelectedStateIso] = useState<string | undefined>(undefined)

  const selectedCountry = findCountry(selectedCountryIso ?? '')
  const sortedStates = useMemo(() => (selectedCountryIso ? subdivisionOptions(selectedCountryIso) : []), [selectedCountryIso])
  const hasStates = sortedStates.length > 0
  const atLimit = locations.length >= locationLimit
  const overLimit = locations.length > locationLimit
  // The same place twice is legitimate - two offices in one canton, say. The name is what has to
  // be unique, and uniqueLocationName handles that below.
  const canAdd = !atLimit && Boolean(selectedCountryIso && (!hasStates || selectedStateIso))

  const handleCountryChange = (iso: string) => {
    setSelectedCountryIso(iso)
    setSelectedStateIso(undefined)
  }

  const handleAdd = () => {
    if (!selectedCountry) return
    const stateEntry = selectedStateIso ? sortedStates.find((s) => s.value === selectedStateIso) : undefined
    const name = uniqueLocationName(
      placeLabel({ countryCode: selectedCountry.iso, subdivisionCode: stateEntry?.value ?? null }),
      locations.map((location) => location.name)
    )
    onAdd({ name, countryCode: selectedCountry.iso, subdivisionCode: stateEntry?.value ?? null })
    setSelectedCountryIso(undefined)
    setSelectedStateIso(undefined)
  }

  const currentYear = new Date().getFullYear()
  const holidaysRepo = useMemo(() => new PublicHolidaysRepository(), [])

  const holidayCounts = useMemo(
    () => Object.fromEntries(locations.map((loc) => [loc.id, holidaysRepo.getHolidaysForCountry(currentYear, loc.countryCode, loc.subdivisionCode ?? undefined, 'public').length])),
    [locations]
  )

  const sortedCountries = useMemo(() => countriesForPicker(), [])

  return (
    <Space direction="vertical" style={{ width: '100%' }} size={16}>
      {readOnly && (
        <div className="vt-readonly-notice">
          <IntlMessages
            id="onboarding.setup.readOnly.message"
            values={{
              link: (
                <Link to={FrontendUrls.locations}>
                  <IntlMessages id="app.locations" />
                </Link>
              ),
            }}
          />
        </div>
      )}
      <Text>
        <IntlMessages id="onboarding.setup.location.intro" values={{ locationName: <Text strong>{locations[0]?.name}</Text> }} />
      </Text>

      <List
        className="vt-location-list"
        dataSource={locations}
        renderItem={(location) => (
          <List.Item
            className="vt-location-list-item"
            actions={
              readOnly || location.isFromSignup
                ? []
                : [
                    <Button
                      key="remove"
                      type="text"
                      size="small"
                      className="vt-location-remove-btn"
                      icon={<PiX size={14} />}
                      aria-label={`Remove ${location.name}`}
                      onClick={() => onRemove(location.id)}
                    />,
                  ]
            }
          >
            <Space direction="vertical" size={2}>
              <Space>
                <PiMapPin size={14} className="vt-location-pin-icon" />
                <Text>{location.name}</Text>
                {location.isFromSignup && (
                  <Tag color="success" className="vt-from-signup-tag">
                    <IntlMessages id="onboarding.setup.location.fromSignupTag" />
                  </Tag>
                )}
              </Space>
              {(holidayCounts[location.id] ?? 0) > 0 && (
                <Space size={4} className="vt-location-holidays-hint">
                  <PiCalendarBlank size={11} />
                  <Text>
                    {holidayCounts[location.id] === 1 ? (
                      <IntlMessages id="onboarding.setup.location.holidays.one" />
                    ) : (
                      <IntlMessages id="onboarding.setup.location.holidays.many" values={{ count: holidayCounts[location.id] }} />
                    )}
                  </Text>
                </Space>
              )}
            </Space>
          </List.Item>
        )}
      />

      {!readOnly &&
        (atLimit ? (
          <Text type="secondary">
            <IntlMessages id="onboarding.setup.location.limitReached" values={{ limit: locationLimit }} />
          </Text>
        ) : (
          <Flex gap={8} className="vt-location-add-row" wrap="wrap">
            <Select
              showSearch
              size="large"
              value={selectedCountryIso}
              onChange={handleCountryChange}
              filterOption={filterCountryOptions}
              placeholder={formatMessage({ id: 'onboarding.setup.location.selectCountryPlaceholder' })}
              style={{ flex: 1, minWidth: 180 }}
            >
              {sortedCountries.map((c) => (
                <Option value={c.iso} key={c.iso}>
                  {c.name}
                </Option>
              ))}
            </Select>

            {hasStates && (
              <Select
                showSearch
                size="large"
                value={selectedStateIso}
                onChange={setSelectedStateIso}
                filterOption={filterOptions}
                placeholder={formatMessage(
                  { id: 'onboarding.setup.location.selectSubdivisionPlaceholder' },
                  { term: formatMessage({ id: subdivisionTermKey(selectedCountryIso ?? '') }) }
                )}
                style={{ flex: 1, minWidth: 180 }}
              >
                {sortedStates.map((s) => (
                  <Option value={s.value} key={s.value}>
                    {s.label}
                  </Option>
                ))}
              </Select>
            )}

            <Button size="large" onClick={handleAdd} disabled={!canAdd}>
              <IntlMessages id="onboarding.setup.button.add" />
            </Button>
          </Flex>
        ))}

      {readOnly && onNext && (
        <Button block size="large" onClick={onNext}>
          <IntlMessages id="onboarding.setup.button.next" />
        </Button>
      )}

      {!readOnly && overLimit && (
        <Alert
          type="warning"
          showIcon
          message={<IntlMessages id="onboarding.setup.location.overLimit" values={{ limit: locationLimit, count: locations.length }} />}
          style={{ marginBottom: 8 }}
        />
      )}

      {!readOnly && (
        <>
          <Button type="primary" block size="large" onClick={onConfirm} disabled={locations.length === 0 || overLimit} className="vt-onboarding-confirm-btn">
            {locations.length <= 1 ? (
              <IntlMessages id="onboarding.setup.location.confirmButton.looksGood" />
            ) : (
              <IntlMessages id="onboarding.setup.location.confirmButton.many" values={{ count: locations.length }} />
            )}
          </Button>

          <div className="vt-onboarding-skip">
            <Typography.Link onClick={onSkip}>
              <IntlMessages id="onboarding.setup.skipForNow" />
            </Typography.Link>
          </div>
        </>
      )}
    </Space>
  )
}

export default LocationStep
